import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import StyleIcon from '@mui/icons-material/Style';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ShieldIcon from '@mui/icons-material/Shield';
import SearchIcon from '@mui/icons-material/Search';
import config from '../config.json';

const highlightIcons = [<CameraAltIcon />, <SmartToyIcon />, <ShieldIcon />, <SearchIcon />];

const highlights = config.products.highlights.map((item, i) => ({
  ...item,
  icon: highlightIcons[i],
}));

const { products } = config;

function Products() {
  const theme = useTheme();

  return (
    <Box
      id="products"
      sx={{
        py: { xs: 10, md: 14 },
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="overline"
            sx={{
              color: 'secondary.main',
              fontWeight: 600,
              letterSpacing: 3,
              fontSize: '0.85rem',
            }}
          >
            {products.overline}
          </Typography>
          <Typography variant="h2" sx={{ mt: 1, mb: 2 }}>
            {products.headingPrefix}
            <Box
              component="span"
              sx={{
                background: theme.custom.gradientPrimary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {products.headingAccent}
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
            }}
          >
            {products.description}
          </Typography>
        </Box>

        {/* Product Overview Card */}
        <Card
          elevation={0}
          sx={{
            mb: 6,
            overflow: 'visible',
            background: theme.custom.productCardBg,
            border: `1px solid ${theme.custom.cardBorder}`,
            borderRadius: 4,
          }}
        >
          <CardContent
            sx={{
              p: { xs: 4, md: 6 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: { xs: 3, md: 5 },
            }}
          >
            {/* App Icon */}
            <Box
              sx={{
                flex: '0 0 auto',
                width: { xs: 100, md: 140 },
                height: { xs: 100, md: 140 },
                borderRadius: 4,
                background: theme.custom.mockupBg,
                border: `2px solid ${theme.custom.mockupBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: theme.custom.mockupShadow,
              }}
            >
              <StyleIcon
                sx={{ fontSize: { xs: 48, md: 64 }, color: 'primary.main' }}
              />
            </Box>

            {/* Product Info */}
            <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}
              >
                <Chip
                  icon={<AppleIcon sx={{ fontSize: 14 }} />}
                  label="iOS"
                  size="small"
                  sx={{
                    bgcolor: theme.custom.chipBg,
                    color: 'primary.light',
                    fontWeight: 600,
                  }}
                />
                {/* <Chip
                  icon={<AndroidIcon sx={{ fontSize: 14 }} />}
                  label="Android"
                  size="small"
                  sx={{
                    bgcolor: theme.custom.chipBg,
                    color: 'primary.light',
                    fontWeight: 600,
                  }}
                /> */}
              </Stack>

              <Typography variant="h3" sx={{ mb: 2 }}>
                {products.productName}
              </Typography>

              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', mb: 3 }}
              >
                {products.productDescription}
              </Typography>

              <Button
                component={RouterLink}
                to="/cardfolio"
                variant="contained"
                color="primary"
                size="large"
                endIcon={<ArrowForwardIcon />}
              >
                {products.exploreButton}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Highlights Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr 1fr',
            },
            gap: 3,
          }}
        >
          {highlights.map((item, index) => (
            <Card
              key={index}
              elevation={0}
              sx={{
                textAlign: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  borderColor: 'primary.main',
                  boxShadow: theme.custom.shadow,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 3,
                    mx: 'auto',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: theme.custom.highlightGradient,
                    color: 'primary.main',
                    '& .MuiSvgIcon-root': {
                      fontSize: 28,
                    },
                  }}
                >
                  {item.icon}
                </Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary' }}
                >
                  {item.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default Products;
